import { Search } from "lucide-react";
import { useCallback } from "react";
import { Box } from "@mui/joy";
import type { GridApi } from "ag-grid-community";

import { Pagination } from "../../shared";

interface PaginationControls {
    currentPage: number;
    totalPage: number;
}

interface GridQuickControlProps {
    gridApi: GridApi | null;
    paginationControls: PaginationControls;
    customToolbarActions?: React.ReactNode;
}

export function GridQuickControl({
    gridApi,
    paginationControls,
    customToolbarActions,
}: GridQuickControlProps) {
    const { currentPage, totalPage } = paginationControls;

    const handleSearchChange = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            if (gridApi) {
                const searchValue = event.target.value;
                if (typeof (gridApi as any).setGridOption === "function") {
                    (gridApi as any).setGridOption("quickFilterText", searchValue);
                } else {
                    (gridApi as any).setQuickFilter?.(searchValue);
                }
            }
        },
        [gridApi]
    );

    const handlePageChange = useCallback(
        (_event: React.ChangeEvent<unknown>, page: number) => {
            if (gridApi) {
                gridApi.paginationGoToPage(page - 1);
            }
        },
        [gridApi]
    );

    const showPagination = totalPage > 1;

    return (
        <Box
            py={1.5}
            px={2}
            display={"flex"}
            alignItems={"center"}
            justifyContent={"space-between"}
            className="grid-toolbox"
        >
            <Box display={"flex"} alignItems={"center"} gap={1} flex={1}>
                {customToolbarActions && (
                    <Box display={"flex"} alignItems={"center"} gap={1} mr={1}>
                        {customToolbarActions}
                        <Box sx={{ borderLeft: "1px solid", borderColor: "divider", height: 16, mx: 0.5 }} />
                    </Box>
                )}
                <Search size={14} />
                <input
                    className="global-search-input"
                    placeholder="Search..."
                    onChange={handleSearchChange}
                    disabled={!gridApi}
                />
            </Box>
            {showPagination && (
                <Pagination
                    variant="plain"
                    shape="rounded"
                    size="sm"
                    count={totalPage}
                    page={currentPage}
                    onChange={handlePageChange}
                    disabled={!gridApi}
                />
            )}
        </Box>
    );
}
